import * as moment from 'moment';
import { ICellRendererParams } from 'ag-grid-community';
import { IMicrositeRecipientForList } from '@nkr/models/index';
import { CustomSiteStatus, MicrositeListType, MicrositeRecipientStatus } from '@nkr/enums/index';
import { MicrositeAction } from '../microsite-action';
import OverlayMenuButton from '@nkr/components/overlay-menu-button';

const menuItemList = [
    { action: MicrositeAction.EditRegistration, title: 'Edit Registration' },
    { action: MicrositeAction.EditProfile, title: 'Edit Profile' },
    { action: MicrositeAction.PatientsProfile, title: 'Patient\'s Profile' },
    { action: MicrositeAction.ViewStarterSite, title: 'View Starter Site' },
    { action: MicrositeAction.ViewCurrentlyActiveCustomSite, title: 'View Active Custom Site' },
    { action: MicrositeAction.PreviewCustomSite, title: 'Preview Custom Site' },
    { action: MicrositeAction.Approve, title: 'Approve' },
    { action: MicrositeAction.Decline, title: 'Decline' },
    { action: MicrositeAction.ModifyCustomSite, title: 'Modify Custom Site (NKR Only)' },
    { action: MicrositeAction.ViewRegistrations, title: 'View Registrations for Intended Recipient' },
    { action: MicrositeAction.ViewCompletedRegistrations, title: 'View Registered Donors' },
    { action: MicrositeAction.ViewRegistrationsNoCenter, title: 'View Registrations for Intended Recipient' },
    { action: MicrositeAction.ViewCompletedRegistrationsNoCenter, title: 'View Registered Donors' },
    { action: MicrositeAction.SendReminderEmail, title: 'Send Reminder Email' },
    { action: MicrositeAction.ResendApprovalEmail, title: 'Resend Approval Email' },
    { action: MicrositeAction.Confirm, title: 'Validate/Confirm' },
    { action: MicrositeAction.Deny, title: 'Deny' },
    { action: MicrositeAction.DownloadCardBatchFile, title: 'Download Card Batch File' },
    { action: MicrositeAction.ViewActivityLog, title: 'View Activity Log' },
    { action: MicrositeAction.ViewLinkedEntities, title: 'View Linked Patients' },
    { action: MicrositeAction.ChangeStatus, title: 'Change Status' },
    { action: MicrositeAction.EditDemographic, title: 'Edit Demographic' },
    { action: -1, title: '' }
];

interface IProps {
    listType: MicrositeListType,
    isAdmin: boolean,
    isAdminLogin: boolean,
    pullRight?: boolean,
    onMenuDisplayed?: (id: number) => void,
    onMenuToggle: (show: boolean) => void,
    onItemSelected: (id: number, action: MicrositeAction) => void
}

export const ActionButtonRenderer = (props: ICellRendererParams & IProps) => {
    const handleMenuToggle = (show: boolean) => {
        props.onMenuToggle(show);
    };

    const handleMenuSelection = (id: any, action: number) => {
        props.onItemSelected(id, action);
    };

    const getUrl = (action: MicrositeAction, id: number): { url: string, newTab: boolean } => {
        let hrefPrefix = props.isAdmin ? '/admin/microsite' : `/center/${props.data.centerId}/microsite`;
        let secureLink = `${__NKR_ORG_URL__}/microsite/custom-site/${id}/${props.data.editKey}`;
        let adminCustomSiteEditLink = `${secureLink}?adminKey=749cfd3b48f9d3f59cff43b4deac8c25`;

        switch (action) {
            case MicrositeAction.EditRegistration: return { url: `${hrefPrefix}/${id}/registration?previous=${props.listType}`, newTab: false };
            case MicrositeAction.EditProfile: return { url: `${hrefPrefix}/${id}/profile?previous=${props.listType}`, newTab: false };
            case MicrositeAction.EditDemographic: return { url: `${hrefPrefix}/${id}/demographic?previous=${props.listType}`, newTab: false };
            case MicrositeAction.PatientsProfile: return { url: secureLink, newTab: false };
            case MicrositeAction.ViewStarterSite: return { url: `${__NKR_ORG_URL__}/${props.data.shortCode}`, newTab: true };
            case MicrositeAction.ViewCurrentlyActiveCustomSite: return { url: `${__NKR_ORG_URL__}/${props.data.shortCode}`, newTab: false };
            case MicrositeAction.PreviewCustomSite: return { url: `${__NKR_ORG_URL__}/${props.data.shortCode}?preview=2`, newTab: false };
            case MicrositeAction.Approve: return { url: `${hrefPrefix}/${id}/approve?previous=${props.listType}`, newTab: false };
            case MicrositeAction.Decline: return { url: `${hrefPrefix}/${id}/decline?previous=${props.listType}`, newTab: false };
            case MicrositeAction.ModifyCustomSite: return { url: adminCustomSiteEditLink, newTab: false };
            case MicrositeAction.ViewRegistrations: return null;
            case MicrositeAction.ViewCompletedRegistrations: return { url: `/center/${props.data.centerId}/donors/donor-intake/search-by-intended?micrositeRecipientId=${id}`, newTab: true };
            case MicrositeAction.ViewRegistrationsNoCenter: return null;
            case MicrositeAction.ViewCompletedRegistrationsNoCenter: return { url: `/admin/dash/donor-intake/search-by-intended?micrositeRecipientId=${id}`, newTab: true };
            case MicrositeAction.SendReminderEmail: return null;
            case MicrositeAction.ResendApprovalEmail: return null
            case MicrositeAction.Confirm: return { url: `${hrefPrefix}/${id}/confirm?previous=${props.listType}`, newTab: false };
            case MicrositeAction.Deny: return { url: `${hrefPrefix}/${id}/deny?previous=${props.listType}`, newTab: false };
            case MicrositeAction.DownloadCardBatchFile: return null;
            case MicrositeAction.ViewActivityLog: return { url: `${hrefPrefix}/${id}/history?previous=${props.listType}`, newTab: false };
            case MicrositeAction.ViewLinkedEntities: return null;
            case MicrositeAction.ChangeStatus: return null;
        }
    };

    const getMenuItems = () => {
        const data = props.data as IMicrositeRecipientForList;
        const confirmPatient = data.currentStatusId == MicrositeRecipientStatus.PendCenterConfirmIsPatient;
        const editDemographic = data.hasMicrositeSurvey;

        const showApproveDecline =
            (data.customSiteStatusId == CustomSiteStatus.PendCenterApproval
                || data.currentStatusId == CustomSiteStatus.PendCtrDeclinedFixable)
            || (props.isAdminLogin && (
                data.customSiteStatusId == CustomSiteStatus.PendNkrDeclinedFixable
                || data.customSiteStatusId == CustomSiteStatus.PendNkrApproval));

        const isInactive = [
            MicrositeRecipientStatus.Inactive,
            MicrositeRecipientStatus.Denied,
            MicrositeRecipientStatus.InvitationSent,
            MicrositeRecipientStatus.PendCenterConfirmIsPatient
        ].indexOf(data.currentStatusId) >= 0;

        const isCustomSiteActive = data.currentStatusId == MicrositeRecipientStatus.Active;

        const isPendingPatient = [
            MicrositeRecipientStatus.InvitationSent,
            MicrositeRecipientStatus.PendCenterConfirmIsPatient
        ].indexOf(data.currentStatusId) >= 0;

        const showEditProfile = [
            CustomSiteStatus.PendCenterApproval,
            CustomSiteStatus.PendCtrDeclinedFixable
        ].indexOf(data.customSiteStatusId) >= 0;

        const isNkrLogin = props.isAdmin;

        const viewStarterSite =
            data.agreedToHipaa
            && data.currentStatusId == MicrositeRecipientStatus.StarterSiteActive;

        const viewCustomSite = data.agreedToHipaa
            && data.currentStatusId == MicrositeRecipientStatus.Active
            && data.customSiteStatusId == CustomSiteStatus.Active;

        const viewStagnantSite =
            data.agreedToHipaa
            && data.currentStatusId == MicrositeRecipientStatus.Stagnant;

        const activePreview = [
            CustomSiteStatus.PendCenterApproval,
            CustomSiteStatus.PendNkrApproval,
            CustomSiteStatus.PendCtrDeclinedFixable,
            CustomSiteStatus.DeclinedUnfixable
        ];

        const previewChanges = activePreview.indexOf(data.currentStatusId) >= 0;

        const previewCustomSite =
            data.agreedToHipaa == true
            && (data.customSiteStatusId < MicrositeRecipientStatus.Active || activePreview.indexOf(data.customSiteStatusId) >= 0)
            && data.hasPendingChanges
            ;

        let menuItems = [];

        if (!confirmPatient) {
            menuItems.push(MicrositeAction.EditRegistration);
            menuItems.push(-1);
        }

        menuItems.push(MicrositeAction.ChangeStatus);

        if (isPendingPatient) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.SendReminderEmail);
        }

        if (showEditProfile) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.EditProfile);
        }

        if (showApproveDecline && !isInactive) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.Approve);
            menuItems.push(MicrositeAction.Decline);
        }

        if (isNkrLogin && isPendingPatient) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.PatientsProfile);
        }

        if (data.agreedToHipaa && !isInactive && (viewStarterSite == true || viewCustomSite == true || previewCustomSite == true)) {
            menuItems.push(-1);

            if (viewStarterSite && !viewCustomSite) {
                menuItems.push(MicrositeAction.ViewStarterSite);
            }

            if (viewCustomSite) {
                menuItems.push(MicrositeAction.ViewCurrentlyActiveCustomSite);
            }

            if (previewCustomSite) {
                menuItems.push(MicrositeAction.PreviewCustomSite);
            }
        }

        menuItems.push(MicrositeAction.ViewActivityLog);

        if (editDemographic) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.EditDemographic);
        }

        let showDeactivationBtn = false;

        if (!isInactive && !confirmPatient) {
            showDeactivationBtn = true;

            if (props.isAdmin && !isInactive && !confirmPatient) {
                menuItems.push(-1);
                menuItems.push(MicrositeAction.ModifyCustomSite);
            }

            menuItems.push(-1);

            if (data.centerId > 0) {
                menuItems.push(MicrositeAction.ViewRegistrations);
                menuItems.push(MicrositeAction.ViewCompletedRegistrations);
            } else {
                menuItems.push(MicrositeAction.ViewRegistrationsNoCenter);
                menuItems.push(MicrositeAction.ViewCompletedRegistrationsNoCenter);
            }

            menuItems.push(MicrositeAction.ViewLinkedEntities);
        }

        if (isCustomSiteActive) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.ResendApprovalEmail);
        }

        if (confirmPatient) {
            menuItems.push(-1);
            menuItems.push(MicrositeAction.Confirm);
            menuItems.push(MicrositeAction.Deny);
        }

        if (viewStagnantSite) {
            menuItems = [];
            menuItems.push(MicrositeAction.EditRegistration);
            menuItems.push(MicrositeAction.ChangeStatus);
            menuItems.push(-1);
            menuItems.push(MicrositeAction.ViewCurrentlyActiveCustomSite);
            menuItems.push(MicrositeAction.ViewActivityLog);
        }


        return menuItems;
    };

    const menuItems = getMenuItems().map(item => {
        return menuItemList.find(x => x.action == item);
    });

    const menu = menuItems.map(item => {
        let url = item.action != -1 ? getUrl(item.action, props.data.id) : null;
        let id = item.action >= 0 ? props.data.id : 'div-' + props.data.id + '-' + Math.random() * 10000;

        if (url == null) {
            if (item.action >= 0) {
                return { id, action: item.action, include: true, selectable: true, element: <>{item.title}</> };
            } else {
                return { id, action: item.action, include: true, selectable: false, element: <div style={{ height: 10 }}></div>, divider: true };
            }
        } else {
            return { id, action: item.action, include: true, selectable: false, element: <a href={url.url} target={url.newTab ? '_blank' : '_self'}>{item.title}</a>, title: item.title };
        }
    });

    return (
        <div style={{ width: '100%', textAlign: 'center' }}>
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                listItemStyle={{ padding: '2px 7.5px', fontSize: 14 }}
                includeCaret={true}
                onToggle={handleMenuToggle}
                onSelect={handleMenuSelection}
            />
        </div>
    );
}
